// src/pages/ProfileGate/steps/LearningPlanStep.jsx

import React, {
  useState,
} from "react";

import {
  requestAndEnablePushNotifications,
} from "../../../lib/pwa/pushClient";

import {
  useNavigate,
} from "react-router-dom";

import {
  OnboardingFormCard,
  OnboardingInterstitial,
} from "../OnboardingVisuals";

import {
  CHECK_IN_TIME_OPTIONS,
  LEARNING_DAY_OPTIONS_V3,
  useProfileGateDraft,
} from "../../../hooks/useProfileGateDraft";

import {
  saveOnboardingDraftProgress,
  saveNotificationPreference,
} from "../../../lib/onboarding/profileGate";

const PLAN_PENGUIN_URL =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Onboarding/Gwin_Book.png";

function SelectButton({
  label,
  selected,
  disabled = false,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "w-full rounded-[16px] border-2 px-4 py-3 text-left",
        "text-[14px] font-semibold leading-snug transition",
        selected
          ? "border-[#FF914D] bg-[#FFF4EC] text-[#222222]"
          : disabled
          ? "cursor-not-allowed border-[#EEEEEE] bg-[#F8F8F8] text-[#BBBBBB]"
          : "border-[#E6E6E6] bg-white text-[#555555] hover:border-[#FF914D]/60",
      ].join(" ")}
    >
      <div className="flex items-center gap-3">
        <span
          className={[
            "flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] border-2",
            selected
              ? "border-[#FF914D] bg-[#FF914D]"
              : "border-[#BCBCBC] bg-white",
          ].join(" ")}
        >
          {selected ? (
            <span className="text-[12px] font-bold leading-none text-white">
              ✓
            </span>
          ) : null}
        </span>

        <span>
          {label}
        </span>
      </div>
    </button>
  );
}

function QuestionHeader({
  title,
  helper,
}) {
  return (
    <div>
      <h2 className="text-[15px] font-bold leading-snug text-[#222222] sm:text-[16px]">
        {title}
      </h2>

      {helper ? (
        <p className="mt-1 text-[13px] leading-relaxed text-[#757575]">
          {helper}
        </p>
      ) : null}
    </div>
  );
}

export default function LearningPlanStep() {
  const navigate =
    useNavigate();

  const {
    loading,
    draft,
    setField,
  } =
    useProfileGateDraft();

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  if (loading) {
    return null;
  }

  const learningDays =
    Array.isArray(
      draft.learning_days
    )
      ? draft.learning_days
      : [];

  const reminderTimes =
    Array.isArray(
      draft.reminder_times
    )
      ? draft.reminder_times
      : [];

  const hasCorrectDayCount =
    learningDays.length === 3;

  const hasCorrectTimeCount =
    reminderTimes.length === 2;

  const canContinue =
    hasCorrectDayCount &&
    hasCorrectTimeCount;

  const toggleLearningDay = (
    day
  ) => {
    setError("");

    const selected =
      learningDays.includes(
        day
      );

    if (selected) {
      setField(
        "learning_days",
        learningDays.filter(
          (item) =>
            item !== day
        )
      );

      return;
    }

    if (
      learningDays.length >=
      3
    ) {
      return;
    }

    setField(
      "learning_days",
      [
        ...learningDays,
        day,
      ]
    );
  };

  const toggleReminderTime = (
    time
  ) => {
    setError("");

    const selected =
      reminderTimes.includes(
        time
      );

    if (selected) {
      setField(
        "reminder_times",
        reminderTimes.filter(
          (item) =>
            item !== time
        )
      );

      return;
    }

    if (
      reminderTimes.length >=
      2
    ) {
      return;
    }

    setField(
      "reminder_times",
      [
        ...reminderTimes,
        time,
      ]
    );
  };

  const handleContinue = async () => {
  if (!canContinue || saving) {
    return;
  }

  setSaving(true);
  setError("");

  // Start the browser permission request directly
  // from the user's Continue button click.
  let notificationPromise;

  try {
    notificationPromise = requestAndEnablePushNotifications();
  } catch (err) {
    notificationPromise = Promise.resolve({
      ok: false,
      error: err?.message || "Notifications unavailable",
    });
  }

  try {
    // Save the user's chosen learning days and times.
    await saveOnboardingDraftProgress(
      {
        ...draft,
        learning_days: learningDays,
        reminder_times: reminderTimes,
      },
      {
        resumeStep: "orientation",
      }
    );

        // Notification setup must not block onboarding.
    try {
      const notificationResult = await notificationPromise;

      if (notificationResult?.ok) {
        await saveNotificationPreference(true);

        console.info(
          "Notifications enabled successfully."
        );
      } else {
        console.info(
          "Notifications were not enabled:",
          notificationResult
        );

        // Preserve an existing enabled preference.
        if (draft.notifications_enabled !== true) {
          await saveNotificationPreference(false);
        }
      }
    } catch (notificationError) {
      console.warn(
        "Notification setup or preference saving failed:",
        notificationError
      );
    }

    navigate("/profile-gate/orientation");
  } catch (err) {
    console.error(
      "Could not save learning plan:",
      err
    );

    setError(
      err?.message ||
        "Could not save your preferences. Please try again."
    );
  } finally {
    setSaving(false);
  }
};

  return (
    <OnboardingInterstitial
      message="One last thing - I need to help you stay on track"
      secondaryMessage=""
      showBack={true}

      showSpeechPenguin={true}
      speechPenguinUrl={
        PLAN_PENGUIN_URL
      }
      speechPenguinClassName="
        w-[105px]
        sm:w-[125px]
        md:w-[140px]
      "

      nextLabel={
        saving
          ? "Saving..."
          : "Continue"
      }
      nextDisabled={
        !canContinue ||
        saving
      }
      onBack={() =>
        navigate(
          "/profile-gate/summary"
        )
      }
      onNext={
        handleContinue
      }
    >
      <div className="mx-auto mt-7 w-full max-w-[560px]">
        <div className="space-y-4">
          <OnboardingFormCard>
            <QuestionHeader
              title="Which 3 days are you most likely to learn with Gwin?"
              helper={`${learningDays.length} of 3 selected`}
            />

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {LEARNING_DAY_OPTIONS_V3.map(
                (
                  day
                ) => {
                  const selected =
                    learningDays.includes(
                      day
                    );

                  const disabled =
                    !selected &&
                    learningDays.length >=
                      3;

                  return (
                    <SelectButton
                      key={
                        day
                      }
                      label={
                        day
                      }
                      selected={
                        selected
                      }
                      disabled={
                        disabled
                      }
                      onClick={() =>
                        toggleLearningDay(
                          day
                        )
                      }
                    />
                  );
                }
              )}
            </div>
          </OnboardingFormCard>

          <OnboardingFormCard>
            <QuestionHeader
              title="What times of the day can I check-in with you?"
              helper={`Choose 2 · ${reminderTimes.length} of 2 selected`}
            />

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {CHECK_IN_TIME_OPTIONS.map(
                (
                  time
                ) => {
                  const selected =
                    reminderTimes.includes(
                      time
                    );

                  const disabled =
                    !selected &&
                    reminderTimes.length >=
                      2;

                  return (
                    <SelectButton
                      key={
                        time
                      }
                      label={
                        time
                      }
                      selected={
                        selected
                      }
                      disabled={
                        disabled
                      }
                      onClick={() =>
                        toggleReminderTime(
                          time
                        )
                      }
                    />
                  );
                }
              )}
            </div>
          </OnboardingFormCard>

          {/* Notification permission explanation */}
          <div className="rounded-[16px] border border-[#FF914D]/40 bg-[#FFF4EC] px-4 py-4">
            <div className="flex items-start gap-3">
              <span className="text-[22px] leading-none">
                🔔
              </span>

              <div>
                <h3 className="text-[14px] font-bold text-[#222222]">
                  Stay on track with Gwin
                </h3>

                <p className="mt-2 text-[13px] leading-relaxed text-[#555555]">
                  When you tap Continue, your device may ask
                  permission to send notifications.
                </p>

                <p className="mt-2 text-[13px] font-semibold leading-relaxed text-[#222222]">
                  Please select "Allow" so Gwin can remind
                  you on your chosen learning days and times.
                </p>

                <p className="mt-2 text-[12px] leading-relaxed text-[#757575]">
                  You can manage notifications later in
                  your device or browser settings.
                  On iPhone, notifications require Gwin
                  to be installed on your Home Screen.
                </p>
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <p className="mt-4 text-center text-sm font-medium text-red-600">
            {error}
          </p>
        ) : null}
      </div>
    </OnboardingInterstitial>
  );
}